import { useFocusTimer } from '@/contexts/FocusTimerContext';
import { useNow } from '@/hooks/ui/useNow';
import { toViewModel } from '../focus/focusViewModel';
import { moodById, type MoodId } from '../mood/moods';
import type { RightNowState } from './RightNow';

/**
 * Which one thing "For right now" offers.
 *
 * A mood answered on this visit wins - the student just told us something,
 * and the reply has to follow. A low mood always wins, so the helplines are
 * never hidden behind a timer. Otherwise a running block, then today's
 * earlier answer, then the gentle default.
 */
export function useRightNow(
  mood: MoodId | null,
  answeredHere: boolean
): RightNowState {
  const { state } = useFocusTimer();
  const active = state.phase !== 'idle';
  const now = useNow(state.phase === 'focus' || state.phase === 'break');
  const option = moodById(mood);

  if (option && (answeredHere || option.low)) {
    return { kind: 'mood', mood: option };
  }
  if (active) {
    const view = toViewModel(state, now);
    return {
      kind: 'block',
      phase: state.phase as Exclude<typeof state.phase, 'idle'>,
      timeLabel: view.timeLabel,
      countsUp: view.progress === null,
      intention: view.intention,
    };
  }
  return option ? { kind: 'mood', mood: option } : { kind: 'start' };
}
